import { color, spacing, media } from "@universityofmaryland/web-token-library";
const sizeReservation = ({ mobile, tablet, desktop, highDef }) => {
  const block = (px) => ({
    minHeight: `${px}px`,
    containIntrinsicSize: `auto ${px}px`
  });
  return {
    ...block(mobile),
    ...tablet != null && {
      [`@media (${media.queries.tablet.min})`]: block(tablet)
    },
    ...desktop != null && {
      [`@media (${media.queries.desktop.min})`]: block(desktop)
    },
    ...highDef != null && {
      [`@media (${media.queries.highDef.min})`]: block(highDef)
    }
  };
};
const createElementStyles = (elementName, additionalStyles = {}) => ({
  [`${elementName}:not(:defined)`]: {
    ...additionalStyles.notDefined
  },
  [`${elementName}:defined`]: {
    ...additionalStyles.defined
  },
  ...additionalStyles.custom
});
const createBatchStyles = (names, prefix, overrides = {}) => names.reduce((acc, name) => {
  const elementName = `${prefix}${name}`;
  return {
    ...acc,
    ...createElementStyles(elementName, overrides),
    [`${elementName}:not(:defined)`]: {
      contentVisibility: "hidden",
      ...overrides.notDefined,
      "& > *": { display: "none" }
    },
    [`${elementName}:defined`]: {
      contentVisibility: "visible",
      containerType: "inline-size",
      display: "block",
      position: "relative",
      ...overrides.defined
    },
    ...overrides.custom
  };
}, {});
const simpleElements = createBatchStyles(
  [
    "alert-page",
    "alert-site",
    "banner-promo",
    "card-video",
    "events-date",
    "logo",
    "media-inline",
    "media-gif",
    "modal",
    "pathway-highlight",
    "person-bio",
    "quote",
    "slider-events-feed",
    "slider-events",
    "stat"
  ],
  "umd-element-",
  {
    notDefined: sizeReservation({ mobile: 120, tablet: 160, desktop: 200 })
  }
);
const feedElements = createBatchStyles(
  [
    "expert-bio",
    "experts-list",
    "experts-grid",
    "expert-in-the-news",
    "events",
    "events-grouped",
    "events-list",
    "in-the-news-grid",
    "in-the-news-list",
    "news",
    "news-list",
    "news-featured"
  ],
  "umd-feed-",
  {
    notDefined: sizeReservation({ mobile: 480, tablet: 640, desktop: 800 })
  }
);
const cardListStyles = [
  "article",
  "card",
  "card-icon",
  "event",
  "event-time",
  "person"
].reduce(
  (acc, type) => ({
    ...acc,
    ...createElementStyles(`umd-element-${type}`, {
      notDefined: {
        contentVisibility: "hidden",
        display: "block",
        ...sizeReservation({ mobile: 360, tablet: 420, desktop: 480 }),
        "& > *": { contentVisibility: "hidden" }
      },
      defined: {
        contentVisibility: "visible",
        containerType: "inline-size",
        display: "block"
      },
      custom: {
        [`umd-element-${type}[display="list"] + umd-element-${type}[display="list"]`]: {
          marginTop: spacing.md,
          paddingTop: spacing.md,
          borderTop: `1px solid ${color.gray.light}`
        },
        [`umd-element-${type}[display="list"][data-theme="dark"]  + umd-element-${type}[data-theme="dark"]`]: {
          marginTop: spacing.md,
          paddingTop: spacing.md,
          borderTop: `1px solid ${color.gray.dark}`
        },
        [`umd-element-${type}[data-display="list"] + umd-element-${type}[data-display="list"]`]: {
          marginTop: spacing.md,
          paddingTop: spacing.md,
          borderTop: `1px solid ${color.gray.light}`
        },
        [`umd-element-${type}[data-display="list"][data-theme="dark"] + umd-element-${type}[data-display="list"][data-theme="dark"]`]: {
          marginTop: spacing.md,
          paddingTop: spacing.md,
          borderTop: `1px solid ${color.gray.dark}`
        }
      }
    })
  }),
  {}
);
const cardOverlay = createElementStyles("umd-element-card-overlay", {
  notDefined: {
    contentVisibility: "hidden",
    display: "block",
    ...sizeReservation({ mobile: 360, tablet: 420, desktop: 480 }),
    "& > *": { contentVisibility: "hidden" }
  },
  defined: {
    contentVisibility: "visible",
    containerType: "inline-size",
    display: "block"
  },
  custom: {
    ["umd-element-card-overlay.size-large"]: {
      minHeight: "320px",
      [`@media (${media.queries.tablet.min})`]: {
        minHeight: "560px"
      }
    },
    ['umd-element-card-overlay[display="list"] + umd-element-card-overlay[display="list"]']: {
      marginTop: spacing.md
    },
    ['umd-element-card-overlay[data-display="list"] + umd-element-card-overlay[data-display="list"]']: {
      marginTop: spacing.md
    }
  }
});
const brandCardStack = createElementStyles("umd-element-brand-card-stack", {
  notDefined: {
    contentVisibility: "hidden",
    display: "block",
    ...sizeReservation({ mobile: 600, tablet: 800, desktop: 1e3 }),
    "& > *": { display: "none" }
  },
  defined: {
    contentVisibility: "visible",
    containerType: "inline-size",
    display: "block"
  }
});
const carousels = createBatchStyles(
  [
    "carousel",
    "carousel-cards",
    "carousel-image",
    "carousel-image-wide",
    "carousel-multiple-image",
    "carousel-people",
    "carousel-thumbnail"
  ],
  "umd-element-",
  {
    notDefined: {
      contentVisibility: "auto",
      display: "block",
      ...sizeReservation({ mobile: 480, tablet: 600, desktop: 700 })
      // Slot content is left visible: a carousel slide is a likely LCP
      // candidate, and the parent's min-height reservation already
      // prevents CLS. Hiding children would disqualify them from LCP.
    },
    defined: {
      contentVisibility: "visible",
      containerType: "inline-size",
      display: "block"
    }
  }
);
const heroes = {
  ...createBatchStyles(
    ["hero", "hero-expand", "hero-logo", "person-hero"],
    "umd-element-",
    {
      notDefined: {
        contentVisibility: "auto",
        display: "block",
        ...sizeReservation({ mobile: 480, tablet: 560, desktop: 640 })
        // Slot content (hero image / headline) is the LCP candidate on
        // most pages — leave it visible so it can be painted and counted.
        // CLS is protected by the parent's min-height reservation.
      },
      defined: {
        contentVisibility: "visible",
        containerType: "inline-size",
        display: "block"
      }
    }
  ),
  ...createBatchStyles(["hero-brand-video", "hero-grid"], "umd-element-", {
    notDefined: {
      contentVisibility: "hidden",
      display: "block",
      ...sizeReservation({ mobile: 600, tablet: 720, desktop: 800 })
    },
    defined: {
      contentVisibility: "visible",
      containerType: "inline-size",
      display: "block"
    }
  }),
  ...createElementStyles("umd-element-hero-minimal", {
    notDefined: {
      contentVisibility: "hidden",
      display: "block",
      ...sizeReservation({ mobile: 240, tablet: 280, desktop: 320 })
    },
    defined: {
      contentVisibility: "visible",
      containerType: "inline-size",
      display: "block"
    }
  })
};
const accordion = createElementStyles("umd-element-accordion-item", {
  notDefined: {
    contentVisibility: "hidden",
    display: "block",
    ...sizeReservation({ mobile: 56, tablet: 64 })
  },
  defined: {
    contentVisibility: "visible",
    containerType: "inline-size",
    display: "block"
  },
  custom: {
    "umd-element-accordion-item + umd-element-accordion-item": {
      marginTop: spacing.min
    }
  }
});
const brandLogoAnimation = createElementStyles(
  "umd-element-brand-logo-animation",
  {
    defined: {
      position: "absolute",
      right: "0",
      top: "-3vw",
      height: "25vw",
      zIndex: "9",
      overflow: "clip",
      containerType: "normal",
      [`@media (${media.queries.highDef.min})`]: {
        top: "-11vw",
        height: "50vw"
      }
    }
  }
);
const breadcrumb = createElementStyles("umd-element-breadcrumb", {
  defined: {
    display: "block",
    marginTop: `${spacing.sm}`
  }
});
const callToAction = createElementStyles("umd-element-call-to-action", {
  defined: {
    containerType: "normal"
  }
});
const footer = createElementStyles("umd-element-footer", {
  notDefined: {
    backgroundColor: color.black,
    display: "none"
  },
  defined: {
    display: "block"
  }
});
const layoutExpand = createElementStyles("umd-layout-image-expand", {
  notDefined: {
    contentVisibility: "hidden",
    ...sizeReservation({ mobile: 600, tablet: 800, desktop: 1e3 }),
    "& > *": { contentVisibility: "hidden" }
  },
  defined: {
    contentVisibility: "visible",
    containerType: "inline-size"
  }
});
const navigationHeader = createElementStyles("umd-element-navigation-header", {
  notDefined: {
    contentVisibility: "hidden",
    containIntrinsicSize: "auto 44px",
    backgroundColor: color.white,
    height: "60px",
    "& > *": { display: "none" }
  },
  defined: {
    containerType: "normal"
  }
});
const navigationItem = createElementStyles("umd-element-nav-item", {
  notDefined: {
    display: "none"
  },
  defined: {
    containerType: "normal",
    display: "block"
  }
});
const navigationDrawer = createElementStyles("umd-element-nav-drawer", {
  notDefined: {
    contentVisibility: "hidden",
    containIntrinsicSize: "44px 44px",
    backgroundColor: color.white,
    height: "44px",
    width: "44px",
    "& > *": { display: "none" }
  }
});
const navigationSlider = createElementStyles("umd-element-navigation-slider", {
  notDefined: {
    contentVisibility: "hidden",
    "& > *": { display: "none" }
  },
  defined: {
    position: "relative",
    zIndex: "99"
  }
});
const navigationSticky = createElementStyles("umd-element-navigation-sticky", {
  notDefined: {},
  defined: {
    position: "relative",
    zIndex: "99999"
  },
  custom: {
    ["umd-element-navigation-sticky"]: {
      position: "relative",
      zIndex: "99999"
    }
  }
});
const navigationUtilityAlert = createElementStyles(
  "umd-element-navigation-utility",
  {
    notDefined: {
      display: "block !important",
      minHeight: "44px !important",
      containIntrinsicSize: "auto !important",
      "& > *": { display: "none" }
    },
    defined: {
      position: "relative",
      zIndex: "99999",
      containerType: "normal",
      "& > *": { display: "block" }
    },
    custom: {
      ["umd-element-navigation-utility"]: {
        minHeight: "44px",
        backgroundColor: color.red
      }
    }
  }
);
const pathway = createElementStyles("umd-element-pathway", {
  notDefined: {
    contentVisibility: "hidden",
    display: "block",
    ...sizeReservation({ mobile: 360, tablet: 480, desktop: 560 })
  },
  defined: {
    contentVisibility: "visible",
    containerType: "inline-size",
    display: "block"
  }
});
const scrollTopTag = "umd-element-scroll-top";
const scrollTop = createElementStyles(scrollTopTag, {
  notDefined: {
    display: "none"
  },
  defined: {
    height: "40px",
    width: "40px",
    display: "block"
  },
  custom: {
    [`* + ${scrollTopTag}`]: { marginTop: spacing.md },
    [`${scrollTopTag}[fixed], ${scrollTopTag}[data-layout-fixed="true"]`]: {
      position: "fixed",
      right: "8px",
      bottom: "10vh",
      zIndex: "9999",
      [`@media (${media.queries.tablet.min})`]: {
        height: "52px",
        width: "52px",
        right: "40px"
      }
    }
  }
});
const sectionIntro = {
  ...["section-intro-wide", "section-intro"].reduce(
    (acc, name) => ({
      ...acc,
      ...createElementStyles(`umd-element-${name}`, {
        notDefined: {
          contentVisibility: "hidden",
          display: "block",
          ...sizeReservation({ mobile: 360, tablet: 480, desktop: 560 })
        },
        defined: {
          contentVisibility: "visible",
          containerType: "inline-size",
          display: "block"
        },
        custom: {
          [`umd-element-section-intro-wide + *, umd-element-section-intro + *`]: {
            marginTop: spacing.lg,
            [`@media (${media.queries.tablet.min})`]: {
              marginTop: `${spacing["2xl"]}`
            }
          }
        }
      })
    }),
    {}
  )
};
const socialSharingTag = "umd-element-social-sharing";
const socialSharing = createElementStyles(socialSharingTag, {
  notDefined: {
    display: "none"
  },
  defined: {
    display: "block"
  },
  custom: {
    [`* + ${socialSharingTag}`]: { marginTop: spacing.md },
    [`${socialSharingTag}[fixed], ${socialSharingTag}[data-layout-fixed="true"]`]: {
      [`@media (${media.queries.tablet.min})`]: {
        position: "fixed",
        left: "0",
        top: "30vh",
        zIndex: "9999"
      },
      [`@media (${media.queries.desktop.min})`]: {
        left: "40px"
      }
    }
  }
});
const stickyColumns = createElementStyles("umd-element-sticky-columns", {
  notDefined: {
    display: "none"
  },
  defined: {
    display: "block"
  }
});
const tabsTag = "umd-element-tabs";
const tabs = createElementStyles(tabsTag, {
  notDefined: {
    "& > *": { display: "none" },
    ...sizeReservation({ mobile: 360, tablet: 480, desktop: 560 })
  },
  defined: {
    display: "block",
    [`& button, & button`]: {
      transition: "color 0.3s ease"
    },
    [`& button:not([aria-expanded="true"]):hover, & button:not([aria-expanded="true"]):focus`]: {
      color: `${color.red} !important`
    }
  },
  custom: {
    [`${tabsTag} + *`]: { marginTop: spacing.lg }
  }
});
const personTabular = createElementStyles("umd-element-person", {
  notDefined: {
    display: "none"
  },
  defined: {
    display: "block"
  },
  custom: {
    ['umd-element-person[data-display="tabular"] + umd-element-person[data-display="tabular"]']: {
      marginTop: spacing.md,
      paddingTop: spacing.md,
      borderTop: `1px solid ${color.gray.light}`
    },
    ['umd-element-person[data-display="tabular"][data-theme="dark"] + umd-element-person[data-display="tabular"][data-theme="dark"]']: {
      marginTop: spacing.md,
      paddingTop: spacing.md,
      borderTop: `1px solid ${color.gray.dark}`
    }
  }
});
const webComponentStyles = {
  ...accordion,
  ...brandCardStack,
  ...brandLogoAnimation,
  ...breadcrumb,
  ...callToAction,
  ...cardListStyles,
  ...cardOverlay,
  ...carousels,
  ...feedElements,
  ...footer,
  ...heroes,
  ...layoutExpand,
  ...navigationDrawer,
  ...navigationHeader,
  ...navigationItem,
  ...navigationSlider,
  ...navigationSticky,
  ...navigationUtilityAlert,
  ...pathway,
  ...personTabular,
  ...scrollTop,
  ...sectionIntro,
  ...simpleElements,
  ...socialSharing,
  ...stickyColumns,
  ...tabs
};
export {
  webComponentStyles
};
//# sourceMappingURL=web-components.js.map
